import { CommonModule } from '@angular/common';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { environment } from '../../environments/environment';

interface PortalDocument {
    type: string;
    label: string;
    uploaded: boolean;
}

interface FleetPortal {
    licensePlate: string;
    vehicleModel: string | null;
    driverFirstName: string | null;
    documents: PortalDocument[];
    completed: boolean;
    expired: boolean;
}

/**
 * The page a driver opens from the mail asking for their vehicle's documents. No login: the token
 * in the link is the authorization, so requests go through an HttpClient without the app's
 * interceptors and never carry a session token.
 */
@Component({
    selector: 'app-fleet-documents-portal',
    templateUrl: './fleet-documents-portal.component.html',
    styleUrls: ['./fleet-documents-portal.component.scss'],
    standalone: true,
    imports: [CommonModule],
})
export class FleetDocumentsPortalComponent implements OnInit {
    readonly portal = signal<FleetPortal | null>(null);
    readonly error = signal<string | null>(null);
    readonly uploading = signal<string | null>(null);
    readonly uploadError = signal<string | null>(null);
    private readonly http: HttpClient;
    private token = '';

    constructor(private route: ActivatedRoute, httpBackend: HttpBackend) {
        this.http = new HttpClient(httpBackend);
    }

    ngOnInit(): void {
        this.token = this.route.snapshot.paramMap.get('token') || '';
        this.http.get<FleetPortal>(`${environment.baseUrl}/fleet-portal/${encodeURIComponent(this.token)}`).subscribe({
            next: (portal) => this.portal.set(portal),
            error: (err) => this.error.set(err?.error?.message || 'Deze link is niet geldig.'),
        });
    }

    onFileSelected(document: PortalDocument, event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = '';
        if (!file) {
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            this.uploadError.set('Het bestand is groter dan 10 MB.');
            return;
        }
        const form = new FormData();
        form.append('documentType', document.type);
        form.append('file', file, file.name);
        this.uploadError.set(null);
        this.uploading.set(document.type);
        this.http.post<FleetPortal>(`${environment.baseUrl}/fleet-portal/${encodeURIComponent(this.token)}/documents`, form).subscribe({
            next: (portal) => {
                this.uploading.set(null);
                this.portal.set(portal);
            },
            error: (err) => {
                this.uploading.set(null);
                this.uploadError.set(err?.error?.message || 'Opladen mislukt. Probeer het opnieuw.');
            },
        });
    }
}
